import { useCallback, useEffect, useState } from 'react';
import { fetchApi } from '../config/api';

/**
 * - unsupported: Browser (oder Dev-Server ohne Service Worker) kann kein Web Push
 * - disabled: Server hat keine VAPID-Schluessel konfiguriert
 * - denied: Der Nutzer hat Benachrichtigungen im Browser blockiert (nur dort wieder aenderbar)
 */
export type PushStatus = 'loading' | 'unsupported' | 'disabled' | 'denied' | 'off' | 'on';

interface PushConfig {
  enabled: boolean;
  publicKey: string | null;
}

// applicationServerKey erwartet die Bytes des base64url-kodierten VAPID-Schluessels.
const urlBase64ToUint8Array = (base64Url: string) => {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

const browserSupportsPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Der Service Worker wird nur im Produktionsbuild registriert (siehe registerServiceWorker.ts) - im Dev-Server
// gibt es also keine Registrierung, und ready wuerde nie aufloesen.
const getRegistration = () => navigator.serviceWorker.getRegistration();

/**
 * Web-Push-Abo dieses Geraets: Status ermitteln sowie ein- und ausschalten. Das Abo gilt pro Browser/Geraet
 * und wird beim Server dem angemeldeten Konto zugeordnet.
 */
export const usePushNotifications = () => {
  const [status, setStatus] = useState<PushStatus>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [config, setConfig] = useState<PushConfig | null>(null);

  const refresh = useCallback(async () => {
    if (!browserSupportsPush()) {
      setStatus('unsupported');
      return;
    }
    try {
      const registration = await getRegistration();
      if (!registration) {
        setStatus('unsupported');
        return;
      }

      const res = await fetchApi('/notifications/push/config');
      const cfg: PushConfig = res?.data ?? res;
      setConfig(cfg);
      if (!cfg?.enabled || !cfg.publicKey) {
        setStatus('disabled');
        return;
      }

      if (Notification.permission === 'denied') {
        setStatus('denied');
        return;
      }

      const subscription = await registration.pushManager.getSubscription();
      setStatus(subscription && Notification.permission === 'granted' ? 'on' : 'off');
    } catch {
      // Ohne Antwort des Servers lassen wir die Funktion einfach ausgeblendet.
      setStatus('unsupported');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    if (!config?.publicKey) return;
    setBusy(true);
    setError(false);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'off');
        return;
      }

      const registration = await getRegistration();
      if (!registration) throw new Error('no service worker');

      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.publicKey),
        }));

      // Auch ein bereits bestehendes Abo erneut melden: es kann von einem anderen Konto stammen (Kontowechsel
      // im selben Browser) oder serverseitig gelöscht worden sein.
      await fetchApi('/notifications/push/subscribe', { method: 'POST', body: JSON.stringify(subscription.toJSON()) });
      setStatus('on');
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }, [config]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(false);
    try {
      const registration = await getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await fetchApi('/notifications/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint: subscription.endpoint }) });
        await subscription.unsubscribe();
      }
      setStatus('off');
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }, []);

  return { status, busy, error, enable, disable };
};
